import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Table, TableBody, TableRow, TableCell, FormGroup, FormControlLabel,
  Checkbox, TextField, Divider, Alert,
} from '@mui/material';
import {
  patientService, TitrationMeasurement,
  TITRATION_REJECT_REASONS, TITRATION_REQUEST_INFO_OPTIONS, OTHER_OPTION,
} from '../../services/patientService';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';

interface Props {
  patientId: number | null;
  patientName?: string | null;
  hmId?: string | null;
  open: boolean;
  onClose: () => void;
  /** Called after a successful approve/reject/request so the caller can refresh. */
  onDone?: () => void;
}

type Mode = 'view' | 'reject' | 'request';

const fmt = (v: number | null): string => (v === null || v === undefined ? '-' : String(v));

// Uniform size for the view-mode action buttons.
const viewBtn = { flex: 1, height: 44, whiteSpace: 'nowrap' as const, px: 1 };

/**
 * "Approve Titration Measurements" task popup. Shows the latest submitted titration
 * measurement snapshot and lets a clinician Approve, Reject (with reason categories),
 * or Request Additional Information. Each button calls a dedicated backend route.
 */
const ApproveTitrationMeasurementsDialog: React.FC<Props> = ({ patientId, patientName, hmId, open, onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [measurement, setMeasurement] = useState<TitrationMeasurement | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [submitting, setSubmitting] = useState(false);

  // reject / request form state
  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState('');

  const options = mode === 'reject' ? TITRATION_REJECT_REASONS : TITRATION_REQUEST_INFO_OPTIONS;
  const otherSelected = selected.includes(OTHER_OPTION);
  const canSubmit = selected.length > 0 && (!otherSelected || text.trim().length > 0);

  const resetForm = useCallback(() => {
    setMode('view');
    setSelected([]);
    setText('');
  }, []);

  useEffect(() => {
    if (!open || !patientId) return;
    resetForm();
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await patientService.getTitrationMeasurements(patientId);
        if (cancelled) return;
        // Review the baseline titration measurement, not the weekly monitoring
        // submissions that reuse the same fields. Latest baseline wins, so a
        // resubmission after "request additional info" is the one reviewed.
        const baselines = rows.filter((r) => r.is_baseline);
        setMeasurement(baselines.length ? baselines[baselines.length - 1] : null);
      } catch {
        if (!cancelled) toast.error('Failed to load titration measurements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, patientId, resetForm]);

  const toggle = (value: string) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleApprove = async () => {
    if (!patientId) return;
    setSubmitting(true);
    try {
      await patientService.approveTitrationMeasurement(patientId);
      toast.success('Measurements approved');
      onDone?.();
      onClose();
    } catch {
      toast.error('Failed to approve measurements');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForm = async () => {
    if (!patientId || !canSubmit) return;
    setSubmitting(true);
    try {
      if (mode === 'reject') {
        await patientService.rejectTitrationMeasurement(patientId, {
          reasons: selected,
          reason_text: otherSelected ? text.trim() : null,
        });
        toast.success('Measurements rejected');
      } else {
        await patientService.requestTitrationAdditionalInfo(patientId, {
          options: selected,
          info_text: otherSelected ? text.trim() : null,
        });
        toast.success('Additional information requested');
      }
      onDone?.();
      onClose();
    } catch {
      toast.error('Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const dataRows: [string, string][] = useMemo(() => {
    if (!measurement) return [];
    const outOfRange = measurement.out_of_range ?? [];
    const bp = measurement.systolic !== null && measurement.diastolic !== null
      ? `${fmt(measurement.systolic)}/${fmt(measurement.diastolic)}`
      : '-';
    return [
      ['Blood pressure', bp],
      ['Heart rate', fmt(measurement.heart_rate)],
      ['Height', fmt(measurement.height)],
      ['Weight', fmt(measurement.weight)],
      ['BMI', fmt(measurement.bmi)],
      ['Out-of-range value(s)', outOfRange.length ? outOfRange.join(', ') : 'None'],
      ['Submission date', measurement.date || '-'],
    ];
  }, [measurement]);

  const title = mode === 'reject'
    ? 'Reject Titration Measurements'
    : mode === 'request'
      ? 'Request Additional Information'
      : 'Approve Titration Measurements';

  const patientHeader = [patientName, hmId ? `HM ID: ${hmId}` : null].filter(Boolean).join(' · ');

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: CLINIC_DIALOG_SX.paper }}
    >
      <DialogTitle sx={CLINIC_DIALOG_SX.title}>{title}</DialogTitle>
      <DialogContent sx={CLINIC_DIALOG_SX.content}>
        {patientHeader && (
          <Typography
            variant="subtitle1"
            sx={{ color: POPUP_COLORS.titleText, fontWeight: 700, textAlign: 'center', mb: 2 }}
          >
            {patientHeader}
          </Typography>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={CLINIC_DIALOG_SX.spinner} /></Box>
        ) : !measurement ? (
          <Alert severity="info">No titration measurements found for this patient.</Alert>
        ) : mode === 'view' ? (
          <Table size="small">
            <TableBody>
              {dataRows.map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: 600, border: 0, width: '45%', color: POPUP_COLORS.titleText }}>{label}</TableCell>
                  <TableCell sx={{ border: 0, color: POPUP_COLORS.titleText }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <>
            <Typography variant="body2" gutterBottom sx={CLINIC_DIALOG_SX.infoText}>
              Select one or more {mode === 'reject' ? 'reason categories' : 'options'}:
            </Typography>
            <FormGroup>
              {options.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                      sx={{ color: POPUP_COLORS.titleText, '&.Mui-checked': { color: POPUP_COLORS.titleText } }}
                    />
                  }
                  label={opt.label}
                  sx={{ '& .MuiFormControlLabel-label': { color: POPUP_COLORS.titleText } }}
                />
              ))}
            </FormGroup>
            {otherSelected && (
              <>
                <Divider sx={{ my: 1 }} />
                <TextField
                  label={mode === 'reject' ? 'Explanation (required)' : 'Details (required)'}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  required
                  sx={CLINIC_DIALOG_SX.select}
                  InputLabelProps={{ sx: CLINIC_DIALOG_SX.inputLabel }}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={CLINIC_DIALOG_SX.actions}>
        {mode === 'view' ? (
          <>
            <Button onClick={onClose} disabled={submitting} sx={{ ...CLINIC_DIALOG_SX.cancelButton, ...viewBtn }}>Cancel</Button>
            <Button onClick={() => { setMode('reject'); setSelected([]); setText(''); }} disabled={submitting || !measurement} sx={{ ...CLINIC_DIALOG_SX.primaryButton, ...viewBtn }}>
              Reject
            </Button>
            <Button onClick={() => { setMode('request'); setSelected([]); setText(''); }} disabled={submitting || !measurement} sx={{ ...CLINIC_DIALOG_SX.primaryButton, ...viewBtn }}>
              Request Adtl Info
            </Button>
            <Button onClick={handleApprove} disabled={submitting || !measurement} sx={{ ...CLINIC_DIALOG_SX.primaryButton, ...viewBtn }}>
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button onClick={resetForm} disabled={submitting} sx={CLINIC_DIALOG_SX.cancelButton}>Back</Button>
            <Button
              onClick={handleSubmitForm}
              disabled={submitting || !canSubmit}
              sx={CLINIC_DIALOG_SX.primaryButton}
            >
              {mode === 'reject' ? 'Confirm Reject' : 'Send Request'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApproveTitrationMeasurementsDialog;
